import { state } from '../store';
import { t } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';
import { rollMinigame, getCurrentTeam, getCurrentPlayerName, setActiveMinigame, pickNextCard, persistActive } from '../engine/TournamentEngine';
import type { MinigameId, TournamentTeam } from '../types/tournament';
import { TEAM_COLORS } from '../types/tournament';

let isRolling = false;
let rollAnimTimer: number | null = null;

declare global {
  interface Window {
    trnRollDice: () => void;
  }
}

function showSuddenDeathModal(teamNames: string): void {
  const existing = document.getElementById('trn-sd-modal-overlay');
  if (existing) return;

  const overlay = document.createElement('div');
  overlay.id = 'trn-sd-modal-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.85);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:24px;';
  overlay.innerHTML = `
    <div style="background:linear-gradient(135deg,#1a0820,#1a1836);border:1px solid rgba(255,23,68,0.5);border-radius:24px;padding:28px;text-align:center;max-width:320px;width:100%;box-shadow:0 0 40px rgba(255,23,68,0.2);">
      <div style="font-size:48px;margin-bottom:12px;">⚡</div>
      <div style="font-family:Inter,sans-serif;font-size:22px;font-weight:900;color:#FF1744;margin-bottom:12px;letter-spacing:-0.5px;">${t('trn_sd_modal_title')}</div>
      <div style="color:#B0ADCF;font-size:14px;font-weight:600;margin-bottom:8px;line-height:1.5;">${t('trn_sd_modal_body').replace('{teams}', teamNames)}</div>
      <div style="color:rgba(176,173,207,0.6);font-size:12px;font-weight:600;margin-bottom:20px;">${t('trn_victory_sudden_death_sub')}</div>
      <button id="trn-sd-modal-ok" style="width:100%;padding:14px;background:linear-gradient(135deg,#FF1744,#FF8C00);border:none;border-radius:9999px;color:white;font-size:16px;font-weight:800;cursor:pointer;">${t('trn_sd_modal_ok')}</button>
    </div>
  `;
  document.body.appendChild(overlay);
  document.getElementById('trn-sd-modal-ok')?.addEventListener('click', () => overlay.remove());
}


window.trnRollDice = () => {
  const tr = state.tournament;
  if (!tr || isRolling) return;
  if (tr.status !== 'lobby') return;

  isRolling = true;
  tr.status = 'rolling';
  persistActive();
  window.renderApp();

  // Анимация рулетки 1.2 сек: меняем эмодзи каждые 100мс
  const cycle = ['🤔','💬','🙌','😂'];
  let i = 0;
  rollAnimTimer = window.setInterval(() => {
    const el = document.getElementById('trn-dice-icon');
    if (el) el.textContent = cycle[i++ % cycle.length];
  }, 100);

  setTimeout(() => {
    if (rollAnimTimer) { clearInterval(rollAnimTimer); rollAnimTimer = null; }
    const mg = rollMinigame();
    if (!mg) {
      isRolling = false;
      window.showToast(t('cards_unavailable'), 'error');
      window.renderApp();
      return;
    }
    setActiveMinigame(mg);
    isRolling = false;
    launchMinigame(mg);
  }, 1200);
};

function launchMinigame(mg: MinigameId): void {
  const tr = state.tournament;
  if (!tr) return;
  const card = pickNextCard(mg);
  if (!card) {
    window.showToast(t('cards_unavailable'), 'error');
    return;
  }
  const team = getCurrentTeam();
  const teamLabel = team ? `${team.emoji} ${team.name}` : '';

  // Запускаем соответствующий движок. ВАЖНО: state.tournament.status === 'playing'
  // → движок отрисует tournament-mode UI (см. TASK 9, 10).
  if (mg === 'emoji') {
    window.startEmojiGame(`🏆 ${teamLabel}`, [card]);
  } else {
    // wai / describe / act → TimerGameEngine
    window.startTimerGame(`🏆 ${teamLabel}`, [card], mg as any);
  }
}


function colorAccent(color: TournamentTeam['color']): string { return TEAM_COLORS[color].accent; }
function colorBar(color: TournamentTeam['color']): string { return TEAM_COLORS[color].bar; }

export function renderTournamentBoard(): string {
  const tr = state.tournament;
  if (!tr) {
    setTimeout(() => window.navTo('home'), 0);
    return `<div class="p-[24px] text-white">No active tournament</div>`;
  }

  // Show SD modal once on first entry to sudden death
  if (tr.isSuddenDeath && !tr.suddenDeathModalShown) {
    tr.suddenDeathModalShown = true;
    persistActive();
    const teamNames = tr.teams.map(tm => `${tm.emoji} ${tm.name}`).join(' vs ');
    setTimeout(() => showSuddenDeathModal(teamNames), 0);
  }

  const team = getCurrentTeam();
  const player = getCurrentPlayerName();
  const target = tr.settings.scoreTarget;

  const teamsTable = tr.teams.map(t => {
    const pct = Math.min(100, (t.score / target) * 100);
    const isCurrent = team?.id === t.id;
    return `
      <div class="p-[10px_14px] rounded-md ${isCurrent ? 'bg-white/10 border border-white/20' : 'bg-white/5 border border-transparent'} flex items-center gap-[10px]">
        <span class="text-[24px]">${t.emoji}</span>
        <div class="flex-1 min-w-0">
          <div class="flex items-baseline justify-between gap-[8px]">
            <div class="font-display text-[14px] font-extrabold ${colorAccent(t.color)} truncate">${sanitizeHtml(t.name)}</div>
            <div class="font-display text-[16px] font-extrabold text-white">${t.score}<span class="text-[11px] text-t-muted">/${target}</span></div>
          </div>
          <div class="h-[6px] bg-white/10 rounded-pill overflow-hidden mt-[4px]">
            <div class="h-full ${colorBar(t.color)} rounded-pill transition-all duration-500" style="width:${pct}%"></div>
          </div>
        </div>
      </div>
    `;
  }).join('');

  const suddenDeathBanner = tr.isSuddenDeath ? `
    <div class="m-[16px_16px_0] p-[14px] bg-[linear-gradient(135deg,rgba(255,23,68,0.15),rgba(255,140,0,0.10))] border border-neon-red/30 rounded-md text-center animate-pulse">
      <div class="font-display text-[16px] font-extrabold text-neon-red">${t('trn_victory_sudden_death')}</div>
      <div class="text-[12px] text-t-secondary font-semibold mt-[2px]">${t('trn_victory_sudden_death_sub')}</div>
    </div>
  ` : '';

  return `
    <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[16px] z-20 scrollbar-none bg-void flex flex-col" id="screen-tournament-board">
      <!-- Header -->
      <div class="flex items-center justify-between p-[16px_16px_0]">
        <div class="w-[38px] h-[38px] bg-white/[0.07] border border-b-glass rounded-full flex items-center justify-center cursor-pointer text-[18px] active:scale-95" data-action="trn-exit-confirm">←</div>
        <div class="font-display text-[16px] font-extrabold text-white">🏆 ${t('trn_title')}</div>
        <div class="text-[11px] text-t-muted font-semibold">${t('trn_board_round').replace('{n}', String(tr.roundNumber))}</div>
      </div>

      ${suddenDeathBanner}

      <!-- Score table -->
      <div class="p-[16px]">
        <div class="text-[11px] font-extrabold tracking-[1.5px] text-t-muted mb-[8px]">${t('trn_board_score')}</div>
        <div class="flex flex-col gap-[8px]">${teamsTable}</div>
      </div>

      <!-- Current turn -->
      <div class="flex-1 flex flex-col items-center justify-center px-[20px] text-center min-h-[260px]">
        ${team ? `
          <div class="text-[12px] font-extrabold tracking-[1.5px] text-t-muted">${t('trn_board_turn_of').replace('{team}', '').trim()}</div>
          <div class="font-display text-[28px] font-extrabold ${colorAccent(team.color)} mt-[4px] flex items-center gap-[8px]">
            <span class="text-[36px]">${team.emoji}</span>
            <span>${sanitizeHtml(team.name)}</span>
          </div>
          ${player ? `
            <div class="mt-[8px] text-[13px] text-t-secondary font-semibold">${t('trn_board_player')} ${player.emoji} <span class="text-white font-bold">${sanitizeHtml(player.name)}</span></div>
          ` : ''}
        ` : ''}

        <!-- Roll dice button -->
        <div class="mt-[24px]">
          <div
            id="trn-dice-icon"
            class="text-[80px] mb-[16px] ${tr.status === 'rolling' ? 'animate-spin' : 'animate-[iconFloat_3s_ease-in-out_infinite]'}"
            style="display:inline-block;"
          >🎲</div>
        </div>
        <button
          class="w-full max-w-[280px] py-[18px] px-[24px] bg-grad-primary rounded-pill text-white font-body text-[18px] font-extrabold shadow-glow-primary active:scale-[0.97] transition disabled:opacity-50"
          data-action="trn-roll-dice"
          ${tr.status === 'rolling' ? 'disabled' : ''}
        >
          ${tr.status === 'rolling' ? t('trn_board_rolling') : t('trn_board_roll')}
        </button>
        ${tr.lastRoundSnapshot ? `
          <button
            class="mt-[12px] text-[13px] font-bold text-t-muted active:text-white transition"
            data-action="trn-undo-round"
          >${t('trn_board_undo')}</button>
        ` : ''}
      </div>
    </div>
  `;
}
